import {createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {usePaginate} from "../PaginateProvider";
import {CovenantsService} from "../../services/Proposals/CovenantsService";

interface ContextProps {
    covenants: any[],
    setCovenants: Dispatch<SetStateAction<any[]>>,
    isLoading: boolean,
    isLoadingItem: boolean,
    error: string,
    params: any,
    setParams: Dispatch<SetStateAction<any>>,
    handleList: () => Promise<any>,
    handleGet: (productId?: number, financialId?: number, covenantGroupId?: number) => Promise<any>,
    handleSave: (payload: any) => Promise<any>,
    handleInative: (id: number | null) => Promise<any>,
    handleDeleteItem: (id: number | null) => Promise<any>,
    handleSaveItem: (payload: any) => Promise<any>,
}

export const CovenantsContext = createContext<ContextProps>({} as ContextProps);

interface CovenantsProviderProps {
    children: ReactNode
}

export const CovenantsProvider = ({children}: CovenantsProviderProps) => {
    const [covenants, setCovenants] = useState<any[]>([]);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingItem, setIsLoadingItem] = useState<boolean>(false);
    const [params, setParams] = useState<any>({page: 1});

    const {setPagesPaginate} = usePaginate();
    const location = useLocation();
    const service = new CovenantsService();

    const handleList = useCallback(async () => {
        if (!location.pathname.includes('/proposal-track/covenants/list')) {
            return false;
        }

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(params);

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setCovenants(_Response.data);
            setPagesPaginate(_Response.pages);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    }, [location.pathname, params]);

    const handleGet = async (productId?: number, financialId?: number, covenantGroupId?: number) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.get({productId, financialId, covenantGroupId});

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setCovenants(_Response.data);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    };


    const handleSave = async (payload: any) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.save(payload);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }


    const handleInative = async (id: number | null) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.delete(id);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }


    const handleSaveItem = async (payload: any) => {
        setIsLoadingItem(true);

        try {
            const [_Response, _Error] = await service.saveItem(payload);

            if (!!_Error) {
                setIsLoadingItem(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoadingItem(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoadingItem(false);
            return e;
        }
    }

    const handleDeleteItem = async (id: number | null) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.deleteItem(id);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }


    useEffect(() => {
        handleList().then();
    }, [handleList]);

    return (
        <CovenantsContext.Provider value={{
            covenants,
            setCovenants,
            error,
            params,
            setParams,
            handleList,
            handleGet,
            isLoading,
            handleInative,
            handleSave,
            handleSaveItem,
            isLoadingItem,
            handleDeleteItem,
        }}>
            {children}
        </CovenantsContext.Provider>
    )
}

export const useCovenants = () => useContext(CovenantsContext);
